import { cloneDeep } from 'lodash';
import { ColorCodeConverter } from '../../../../utilities/colorcode-converter';
import { TrimUiManager } from '../../ui/trim.ui.manager';
import { TrimTextAlignStrType, TrimTextController } from './trim.text-controller';
import { TrimCjContainer, TrimCjShape, TrimCjText } from '../../../model/cj-factory';
import { TrimImageEvent } from '../image/trim.image-event';
import { TrimCjShadow } from '../../../model/cj-factory/trim.cj-shadow';
import { TrimFontSizeList } from '../../../model/trim.font-size-list';
import { CJ_CONTAINER_NAME, CJ_FOCUS_NAME } from '../../../model/cj-factory/trim.cj-obj-name.collection';
import { TrimCjTool } from '../../../../utilities/trim.cj-tool';
import { TrimEditableEvent } from '../trim.editable-event';
import { TrimPosManager } from '../../pos/trim.pos.manager';
import { TrimDataManager } from '../../data/trim-data.manager';
import { TEXT_SIZE_TEST_VAL } from '../../../../layout-editor/manager/text-image/text-image.manager';
import { TrimImageEditManager } from '../trim.image-edit.manager';
import { TextFont } from '../../../../components/pages/layout/toolbar/layout.free-text';

export type TextFontStyle = TextFont & {outline: number}
export class TrimTextEvent {
  private stage: TrimCjContainer;
  private readonly pageId: string;


  private textController!: TrimTextController;
  private imageEvent!: TrimImageEvent;
  private editableEvent!: TrimEditableEvent;
  private posManager!: TrimPosManager;
  private trimDataManager!: TrimDataManager;

  isFocus: boolean = false;
  isActive: boolean = false;
  isInsert: boolean = false;
  targetFrame: TrimCjContainer | null = null;

  private clicked: boolean = false;
  private dragPointX: number = 0;
  private dragPointY: number = 0;

  constructor(stage: TrimCjContainer, pageId: string) {
    this.stage = stage;
    this.pageId = pageId;
  }

  di(
    textController: TrimTextController,
    imageEvent: TrimImageEvent,
    editableEvent: TrimEditableEvent,
    posMng: TrimPosManager,
    layoutDataMng: TrimDataManager,
  ): void {
    this.textController = textController;
    this.imageEvent = imageEvent;
    this.editableEvent = editableEvent;
    this.posManager = posMng;
    this.trimDataManager = layoutDataMng;
  }

  initialize(): void {
  }

  destroy(): void {
    this.isFocus = false;
    this.targetFrame = null;
    this.clicked = false;
    this.dragPointX = 0;
    this.dragPointY = 0;
  }

  update(stage: TrimCjContainer): void {
    this.stage = stage;
  }
  static splitByNewline(text: string): string[] {
    return text.split(/\r\n|\r|\n/);
  }

  static normalizeToCRLF(lines: string[]): string {
    return lines.join('\r\n');
  }

  /**
   * テキストの実際の描画内容（グリフ）に基づいてピクセルベースのバウンディングボックスを計測する。
   * Canvas に文字列を描画し、不透明ピクセルから文字の実表示領域を計算。
   *
   * @param bounds - 元のバウンディングボックス（位置基準用）
   * @param _textStr - 描画するテキスト文字列
   * @param font - 使用するフォント（例: "MS Gothic"）
   * @returns 実際の描画ピクセルに基づいた x/y/width/height の補正後バウンディングボックス
   */
  static measure(bounds: createjs.Rectangle, font: TextFontStyle, _textStr?: string) {
    const textStr = _textStr !== '' ? _textStr : TEXT_SIZE_TEST_VAL;
    // 引数が不足している場合は元の bounds をそのまま返す
    if (!textStr) return bounds;

    // オフスクリーン canvas を作成
    const canvas = document.createElement("canvas");
    // canvas.width = TrimImageEditManager.CANVAS_W/10;
    // canvas.height = TrimImageEditManager.CANVAS_H/10;
    canvas.width = bounds.width > 500 ? bounds.width : 500;
    canvas.height = bounds.height > 200 ? bounds.height : 200;
    // canvas.width = 500;
    // canvas.height = 200;
    const ctx = canvas.getContext("2d");
    if (!ctx) return bounds;

    // フォント設定と描画スタイル
    ctx.font = `${font.size} ${font.family}`.trim();
    // ctx.font = `${fontSize} ${font}`;

    ctx.textBaseline = "top";  // 上端を基準に描画 → (0, 0) に配置しやすくする
    ctx.fillStyle = "#000";
    ctx.fillText(textStr, 0, 0); // 実際の文字を描画

    // 描画結果を取得（RGBA ピクセル配列）
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    // グリフの上下左右端を初期化（極値）
    let top = canvas.height, bottom = 0;
    let left = canvas.width, right = 0;
    const size = Number(font.size.replace('px', ''));

    // ピクセル単位で alpha 値をスキャン → 描画されている範囲を探索
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = imageData[(y * canvas.width + x) * 4 + 3]; // アルファチャンネル（不透明度）
        if (alpha > 0) {
          if (y < top) top = y;
          if (y > bottom) bottom = y;
          if (x < left) left = x;
          if (x > right) right = x;
        }
      }
    }

    // 元の bounds に対して相対位置で補正した新しい矩形を返す

    return {
      x: bounds.x + left,
      y: bounds.y + top,
      width: _textStr !== '' ? right - left + Math.floor(size/10) + font.outline : 0,
      height: bottom - top + Math.floor(size/10) + font.outline

    };
  }

  /**
   * テキストの実際の描画内容（グリフ）に基づいてピクセルベースのバウンディングボックスを計測する。
   * Canvas に文字列を描画し、不透明ピクセルから文字の実表示領域を計算。
   *
   * @param bounds - 元のバウンディングボックス（位置基準用）
   * @param textStr - 描画するテキスト文字列
   * @param font - 使用するフォントおよびテキストスタイル（例: "bold italic 10px MS Gothic"）
   * @returns 実際の描画ピクセルに基づいた x/y/width/height の補正後バウンディングボックス
   */
  static measureActualGlyphBox(bounds: createjs.Rectangle, font: TextFontStyle, textStr?: string) {
    // 引数が不足している場合は元の bounds をそのまま返す
    if (!textStr) return bounds;
    const textList = TrimTextEvent.splitByNewline(textStr);
    const resultBounds = {x: bounds.x, y: bounds.y, height: 0, width: 0};
    for (const text of textList) {
      const _bounds = TrimTextEvent.measure(bounds, font, text);
      resultBounds.height += TrimTextEvent.getLineHeight(font) ?? _bounds.height;
      resultBounds.width = _bounds.width > resultBounds.width ? _bounds.width : resultBounds.width;
    }
    return resultBounds;
  }

  static getLineHeight(font: TextFontStyle) {
    const textStr = TEXT_SIZE_TEST_VAL;

    // オフスクリーン canvas を作成
    const canvas = document.createElement("canvas");
    // canvas.width = TrimImageEditManager.CANVAS_W/10;
    // canvas.height = TrimImageEditManager.CANVAS_H/10;
    canvas.width = 500;
    canvas.height = 200;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // フォント設定と描画スタイル
    ctx.font = `${font.isBold} ${font.isItalic} ${font.size} ${font.family}`.trim();
    // ctx.font = `${fontSize} ${font}`;
    ctx.textBaseline = "top";  // 上端を基準に描画 → (0, 0) に配置しやすくする
    ctx.fillStyle = "#000";
    ctx.fillText(textStr, 0, 0); // 実際の文字を描画

    // 描画結果を取得（RGBA ピクセル配列）
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    // グリフの上下左右端を初期化（極値）
    let top = canvas.height, bottom = 0;
    let left = canvas.width, right = 0;

    // ピクセル単位で alpha 値をスキャン → 描画されている範囲を探索
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = imageData[(y * canvas.width + x) * 4 + 3]; // アルファチャンネル（不透明度）
        if (alpha > 0) {
          if (y < top) top = y;
          if (y > bottom) bottom = y;
          if (x < left) left = x;
          if (x > right) right = x;
        }
      }
    }
    const size = Number(font.size.replace('px', ''));
    // 元の bounds に対して相対位置で補正した新しい矩形を返す
    return bottom - top + Math.floor(size/10) + font.outline;
  }

  setFocus(container: TrimCjContainer, isRestore: boolean, callback?: () => void) {
    if (this.targetFrame || this.imageEvent.targetFrame || !isRestore) {
      this.editableEvent.disFocused(this.targetFrame!);
      this.imageEvent.targetFrame && this.editableEvent.disFocused(this.imageEvent.targetFrame);
    }
    if (!isRestore) {
      this.isFocus = true;
      this.targetFrame = container;
    }
    TrimCjTool.checkLoad(container).then((v) => {
      const _data = v.getTransformedBounds();
      const font: TextFontStyle = {
        isBold: container.bold ?? '',
        isItalic: container.italic ?? '',
        family: container.fontName ?? 'ＭＳ ゴシック',
        size: container.textSize ?? '50px',
        outline: Number(container.borderSize ?? 0)
      }
      // const data = TrimTextEvent.measureActualGlyphBox(_data, font, container.text);
      const data = container.getTransformedBounds();
      const focusContainer = new TrimCjContainer();
      focusContainer.name = CJ_CONTAINER_NAME.focus.text;
      const focus: TrimCjShape = new TrimCjShape();
      focus.name = CJ_FOCUS_NAME.line;
      focusContainer.addChild(focus);
      focus.graphics.s('#A5B9F4');
      focus.graphics.sd([10, 5], 0);
      // focus.graphics.beginStroke('#A5B9F4');
      // focus.graphics.setStrokeStyle(2);
      focus.graphics.dr(data.x - 5 - container.x, data.y - 5 - container.y, data.width + 10, data.height + 10);
      const corner: TrimCjShape = new TrimCjShape();
      corner.graphics.f('#A5B9F4').dc(data.x - 5 + data.width + 10 - container.x, data.y - 5 - container.y, 3);
      corner.graphics.f('#A5B9F4').dc(data.x - 5 + data.width + 10 - container.x, data.y - 5 + data.height + 10 - container.y, 3);
      corner.graphics.f('#A5B9F4').dc(data.x - 5 - container.x, data.y - 5 + data.height + 10 - container.y, 3);
      corner.graphics.f('#A5B9F4').dc(data.x - 5 - container.x, data.y - 5 - container.y, 3);
      corner.name = CJ_FOCUS_NAME.corner;
      focusContainer.addChild(corner);
      focusContainer.visible = !isRestore;
      v.addChild(focusContainer);
      callback?.();
    });
  }

  showFocus(container: TrimCjContainer) {
    if (this.targetFrame?.textId !== container.textId && (this.targetFrame || this.imageEvent.targetFrame)) {
      this.editableEvent.disFocused(this.targetFrame!);
      this.imageEvent.targetFrame && this.editableEvent.disFocused(this.imageEvent.targetFrame);
    }
    this.updateFocus(container);
    this.isFocus = true;
    this.targetFrame = container;
    const textFocus = TrimCjTool.getTextFocus(container);
    textFocus.visible = true;
  }

  updateFocus(container: TrimCjContainer) {
    this.isFocus = true;
    this.targetFrame = container;
    const textFocus = TrimCjTool.getTextFocus(container);
    const text = TrimCjTool.getText(container);
    const line = textFocus.getChildByName(CJ_FOCUS_NAME.line) as TrimCjShape;
    const corner = textFocus.getChildByName(CJ_FOCUS_NAME.corner) as TrimCjShape;
    line.graphics.clear();
    corner.graphics.clear();
    const data = container.getTransformedBounds();
    // const _data = container.getTransformedBounds();
    const font: TextFontStyle = {
      isBold: container.bold ?? '',
      isItalic: container.italic ?? '',
      family: container.fontName ?? 'ＭＳ ゴシック',
      size: container.textSize ?? '50px',
      outline: Number(container.borderSize ?? 0)
    }
    // const data = TrimTextEvent.measureActualGlyphBox(_data, font, container.text);
    line.graphics.s('#A5B9F4');
    line.graphics.sd([10, 5], 0);
    line.graphics.dr(data.x - 5 - container.x, data.y - 5 - container.y, data.width + 10, data.height + 10);
    corner.graphics.f('#A5B9F4').dc(data.x - 5 + data.width + 10 - container.x, data.y - 5 - container.y, 3);
    corner.graphics.f('#A5B9F4').dc(data.x - 5 + data.width + 10 - container.x, data.y - 5 + data.height + 10 - container.y, 3);
    corner.graphics.f('#A5B9F4').dc(data.x - 5 - container.x, data.y - 5 + data.height + 10 - container.y, 3);
    corner.graphics.f('#A5B9F4').dc(data.x - 5 - container.x, data.y - 5 - container.y, 3);
    textFocus.rotation = -container.rotation;
    this.stage.stage.update();
  }

  textEditor(text: TrimCjText, container: TrimCjContainer) {
    // this.textController.textImage = text;
    // this.textController.backupText = lodash.cloneDeep(text);

    let textShadow: TrimCjShadow;
    let shadowColor: string = '#000000';
    let shadowOffset: number = 10;
    let shadowAlpha: number = 0;
    if (text.shadow) {
      textShadow = text.shadow;
      shadowColor = textShadow.color ?? '#000000';
      shadowOffset = textShadow.offsetX ?? 10;
      shadowAlpha = ColorCodeConverter.rgbaToColorCode(shadowColor ?? 'rgba(0,0,0,0.65)').alpha;
    }
    // const fontLabel = text.fontData?.size ?? '24pt';
    const fontLabel = text.fontData?.size.replace('px', 'pt') ?? '24pt';
    const fontSize = TrimFontSizeList.find((v) => v.label === fontLabel);

    const textBounds = container.getTransformedBounds();
    const pos = this.posManager.canvasToBrowser({
      x: container.x,
      y: container.y,
    });
    const adjustedPos = this.posManager.canvasToBrowser({
      x: container.x,
      y: container.y + textBounds.height + 10, // 10 ... 微調整用
    });
    TrimUiManager.ins.emit('t->r:layout-menu-disabled', { isDisabled: true });
    TrimUiManager.ins.emit('t->r: post-text-pos', { pos });
    this.textController.backupText = cloneDeep(text);
    this.textController.backupTextContainer = cloneDeep(container);
    // --- テキストツール表示 ---
    TrimUiManager.ins.emit('t->r:show-text-tool', {
      isOpen: true,
      textInfo: {
        id: text.textId ?? '',
        isBold: !!text.fontData?.isBold ?? false,
        isItalic: !!text.fontData?.isItalic ?? false,
        textColor: text.color,
        isOnDropper: false,
        fontType: text.options?.fontType ?? 'server',
        font: text.fontData?.family ?? '',
        // fontSize: text.fontData?.size.replace('pt', '') ?? '11',
        fontSize: text.fontData?.size.replace('pt', '') ?? '24',
        textTransparency: Math.floor(Number(text.alpha) * 100) ?? 100,
        dropShadow: text.options?.isShadow ?? false,
        transparency: shadowAlpha ? Math.floor(Number(shadowAlpha) * 100) : 65,
        distance: shadowOffset,
        outlineWidth: (text.options?.outline ?? 0) / 2,
        outlineColor: text.options?.outlineColor ?? '#000000',
        textAlign: this.textController.textAlignFormat(text.textAlign as TrimTextAlignStrType) ?? '0',
        textRotation: text.parent.rotation,
        textSelectRotation: text.parent.rotation,
        lineSpacingNum: Math.floor(Number(text.lineHeight) - text.getMeasuredLineHeight()),
        textAreaContents: text.text ?? '',
        height: textBounds.height,
        pos,
        adjustedPos,
      },
    });
  }

  handleDown(container: TrimCjContainer, e: any) {
    if (container !== this.targetFrame) return;
    this.dragPointX = this.stage.stage.mouseX - container.x;
    this.dragPointY = this.stage.stage.mouseY - container.y;
  }

  // -- containerの移動 --
  handleMove(container: TrimCjContainer, e: any) {
    if (container !== this.targetFrame) return;
    if (!this.isFocus) return;
    // document.body.style.cursor = 'move';
    container.x = this.stage.stage.mouseX - this.dragPointX;
    container.y = this.stage.stage.mouseY - this.dragPointY;
  }

  handleUp(container: TrimCjContainer, e: any) {
    if (container !== this.targetFrame) return;
    if (!this.isFocus) return;
  }

  // - textへのイベント設定 -
  setTextEvent(text: TrimCjText, container: TrimCjContainer) {
    text.on('click', (e: any) => {
      e.stopPropagation();
      if (this.isInsert) return;
      const frameLength = this.stage.numChildren;
      container && this.stage.setChildIndex(container, frameLength - 1);
      // --- ダブルクリックの場合 ---
      if (this.clicked) {
        this.showFocus(container);
        this.clicked = false;
        this.textEditor(text, container);
        return;
      }
      // --- シングルクリックの場合 ---
      this.clicked = true;
      // --- ダブルクリック受付時間 ---
      setTimeout(() => {
        // ---- ダブルクリックだとfalse ----
        if (this.clicked) {
          this.showFocus(container);
        }
        this.clicked = false;
      }, 250);
    });
    text.on('mousedown', (e) => {
      this.handleDown(container, e);
    });
    text.on('pressmove', (e) => {
      this.handleMove(container, e);
    });
    text.on('pressup', (e) => {
      this.handleUp(container, e);
    });
  }
}
